import { ViewerSidebarContent, type ViewerSidebarPanel } from '../../sidebar-layout'
import type { DocumentViewerMessage } from '@st/document-viewer-kit'
import {
  type AnnotationLocalMessage,
  selAnnotations,
  selAnnotationsPluginState,
  toAnnotationsPluginMessage
} from '@st/document-viewer-kit/plugins/annotations'
import {
  CommentCard,
  CommentComposer,
  ContextMenu,
  ContextMenuItem,
  PencilIcon,
  TrashIcon
} from '@st/theme'
import { formatISO8601Date } from '@st/util/time'
import { match, P } from '@st/pattern'
import { folderMembershipSide } from '~/features/st-folder-viewer/folder-membership-side'
import type { STFolderModuleState } from '~/features/st-folder-viewer/st-folder-types'
import { useSelect } from '~/store'
import { isCommentAnnotation, type ViewerCommentAnnotation } from '../../viewer-annotations'

const selCommentsSidebar = (state: STFolderModuleState) => {
  const viewer = state.viewer
  return {
    comments: Object.values(selAnnotations(viewer, isCommentAnnotation)).map((comment) => ({
      comment,
      author: commentAuthor(state, comment)
    })),
    selectionState: selAnnotationsPluginState(viewer).selectionState
  }
}

export function commentsSidebarPanel(
  sendViewerMessage: (message: DocumentViewerMessage) => void
): ViewerSidebarPanel {
  return {
    title: 'Comments',
    className: 'bg-stone-500',
    content: <CommentsSidebar sendViewerMessage={sendViewerMessage} />
  }
}

function CommentsSidebar({
  sendViewerMessage
}: {
  sendViewerMessage: (message: DocumentViewerMessage) => void
}) {
  const sendAnnotationMessage = (message: AnnotationLocalMessage) =>
    sendViewerMessage(toAnnotationsPluginMessage(message))

  const { comments, selectionState } = useSelect('stFolder', selCommentsSidebar)

  return (
    <ViewerSidebarContent>
      <div className="flex flex-col gap-4 p-4">
        {comments.map(({ comment, author }) => {
          const selectionStatus = match(selectionState)
            .with(
              { type: P.union('selected', 'editing'), annotationId: comment.id },
              ({ type }) => type
            )
            .otherwise(() => 'none' as const)

          if (selectionStatus == 'editing' && !comment.readOnly) {
            return (
              <CommentDraftComposer
                key={comment.id}
                comment={comment}
                send={sendAnnotationMessage}
              />
            )
          }

          return (
            <CommentCard
              key={comment.id}
              title={
                <span className="flex min-w-0 gap-1">
                  <span className="truncate">{author.subject}</span>
                  {author.isClient && <span className="shrink-0">(client)</span>}
                </span>
              }
              time={formatISO8601Date(comment.time)}
              selected={selectionStatus != 'none'}
              onClick={() =>
                sendAnnotationMessage({
                  type: 'selectAndScrollAnnotation',
                  annotationId: comment.id
                })
              }
              menu={commentMenu(comment, sendAnnotationMessage)}
            >
              {comment.content}
            </CommentCard>
          )
        })}
      </div>
    </ViewerSidebarContent>
  )
}

function commentAuthor(state: STFolderModuleState, comment: ViewerCommentAnnotation) {
  if (state.status != 'ready') return { subject: '', isClient: false }
  const user = state.folderState.users.find((candidate) => candidate.id == comment.authorId)
  const subject = user?.name ?? user?.email ?? ''
  const isClient =
    !!comment.authorId &&
    folderMembershipSide(state.folderState.memberships, comment.authorId) == 'client'
  return { subject, isClient }
}

function commentMenu(
  comment: ViewerCommentAnnotation,
  send: (message: AnnotationLocalMessage) => void
) {
  if (comment.readOnly) return undefined

  return (
    <ContextMenu>
      <ContextMenuItem
        icon={<PencilIcon className="size-3" />}
        onClick={() => send({ type: 'beginEditingAnnotation', annotationDraft: comment })}
      >
        Edit
      </ContextMenuItem>
      <ContextMenuItem
        icon={<TrashIcon className="size-3" />}
        onClick={() => send({ type: 'deleteAnnotation', annotationId: comment.id })}
      >
        Delete
      </ContextMenuItem>
    </ContextMenu>
  )
}

function CommentDraftComposer({
  comment,
  send
}: {
  comment: ViewerCommentAnnotation
  send: (message: AnnotationLocalMessage) => void
}) {
  return (
    <CommentComposer
      autoFocus={true}
      placeholder="Write a comment..."
      value={comment.content}
      onChange={(value) => {
        send({ type: 'updateAnnotationDraft', annotationDraft: { ...comment, content: value } })
      }}
      onClickCancel={() => {
        send({ type: 'discardAnnotationDraft', annotationId: comment.id })
      }}
      onClickAdd={() => {
        send({ type: 'commitAnnotationDraft', annotationDraft: comment })
        send({ type: 'selectAnnotation', annotationId: comment.id })
      }}
    />
  )
}
